import React from 'react';
import * as S from './stylesV2';
import TextOrStatus from '../../textOrStatus';
import { BodyM, TaglineP } from '../../../styles/stylesTypography';
import { theme } from '../../../styles/theme';

const cellLabelStyle = {
  width: 'auto',
  height: 'auto',
  gap: '4px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
};

const textInCellLabelStyle = {
  height: '23px',
  display: 'flex',
  alignItems: 'center'
};

const TableWithLabelBlackV2 = ({
  columns,
  data,
  agronomos = [],
  items,
  showAll = false,
  page = 1,
  itemsPerPage = 4,
  serverSide = false,
  onCountChange,
  getRowId,
  onRowClick,
  isRowDisabled,
  getRowStyle
}) => {
  const cellRefs = React.useRef([]);
  const scrollCellRefs = React.useRef([]);
  const [rowHeights, setRowHeights] = React.useState([]);
  const [overflowMap, setOverflowMap] = React.useState({});

  const setCellRef = (rowIdx, colIdx, el) => {
    if (!cellRefs.current[rowIdx]) {
      cellRefs.current[rowIdx] = [];
    }
    cellRefs.current[rowIdx][colIdx] = el;
  };

  const setScrollCellRef = (rowIdx, colIdx, el) => {
    if (!scrollCellRefs.current[rowIdx]) {
      scrollCellRefs.current[rowIdx] = [];
    }
    scrollCellRefs.current[rowIdx][colIdx] = el;
  };

  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);

    const opcoesData = { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' };
    const dataFormatada = data.toLocaleDateString('pt-BR', opcoesData);

    const opcoesHora = { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' };
    const horaFormatada = data.toLocaleTimeString('pt-BR', opcoesHora);

    return `${dataFormatada.replace('.', '').split(' de ').join('/')}, ${horaFormatada}`;
  };

  const isGeneric = Array.isArray(columns) && columns.length > 0;

  const list = isGeneric
    ? (Array.isArray(data) ? data : (Array.isArray(items) ? items : []))
    : (Array.isArray(agronomos) && agronomos.length > 0 ? agronomos : (Array.isArray(items) ? items : []));

  const pickPrimaryCrea = (agronomo) => {
    const creas = Array.isArray(agronomo?.agro_creas) ? agronomo.agro_creas : [];
    return creas.find((crea) => crea?.status === 1) ?? creas[0] ?? null;
  };

  const getDisplayCreas = (agronomo) => {
    const creas = Array.isArray(agronomo?.agro_creas) ? agronomo.agro_creas : [];
    const active = creas.filter((crea) => crea?.status === 1);
    const inactive = creas.filter((crea) => crea?.status !== 1);
    return [...active, ...inactive];
  };

  const deriveStatus = (agronomo) => {
    const raw =
      agronomo?.status ??
      agronomo?.agronomist_status ??
      agronomo?.agronomo_status ??
      agronomo?.agro_agronomists_status ??
      agronomo?.agro_agronomist_status ??
      agronomo?.agro_creas_status ??
      agronomo?.crea_status ??
      pickPrimaryCrea(agronomo)?.status;

    if (raw == null) return { known: false, isActive: false };
    if (typeof raw === 'boolean') return { known: true, isActive: raw };

    if (typeof raw === 'number') {
      if (raw === 0) return { known: true, isActive: false };
      if (raw === 1) return { known: true, isActive: true };
      return { known: true, isActive: raw > 0 };
    }

    const normalized = String(raw).trim().toLowerCase();
    if (!normalized) return { known: false, isActive: false };
    if (normalized === '0') return { known: true, isActive: false };
    if (normalized === '1') return { known: true, isActive: true };
    if (normalized.includes('inativo')) return { known: true, isActive: false };
    if (normalized.includes('ativo')) return { known: true, isActive: true };
    return { known: true, isActive: false };
  };

  const formatRegistro = (crea, agronomo) => {
    const uf = crea?.estado ?? agronomo?.estado ?? '-';
    return uf ? String(uf).toUpperCase() : '-';
  };

  const formatCreaNumero = (crea, agronomo) => {
    const numero = crea?.crea ?? agronomo?.crea_numero ?? '-';
    return numero ?? '-';
  };

  const formatTipoRegistro = (crea) => {
    const tipo = crea?.tipo ?? '-';
    return tipo ? String(tipo).toUpperCase() : '-';
  };

  const total = list.length;
  const startIndex = page && itemsPerPage ? (page - 1) * itemsPerPage : 0;
  const endIndex = itemsPerPage ? startIndex + itemsPerPage : 16;

  const visibleItems = showAll
    ? (serverSide ? list.slice(0, itemsPerPage || 8) : list.slice(startIndex, endIndex))
    : list.slice(0, 16);

  React.useEffect(() => {
    if (typeof onCountChange === 'function') {
      onCountChange(total, visibleItems.length);
    }
  }, [page, itemsPerPage, showAll, total, visibleItems.length, agronomos, items, data]);

  React.useEffect(() => {
    const calculateHeights = () => {
      const newHeights = visibleItems.map((_, rowIdx) => {
        const refsByRow = Array.isArray(cellRefs.current[rowIdx]) ? cellRefs.current[rowIdx] : [];
        const heights = refsByRow.map((el) => {
          if (!el) return 0;
          return el.getBoundingClientRect().height;
        });
        return Math.max(...heights, 0);
      });
      setRowHeights(newHeights);
    };

    const frame = window.requestAnimationFrame(calculateHeights);
    window.addEventListener('resize', calculateHeights);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', calculateHeights);
    };
  }, [visibleItems, isGeneric]);

  React.useEffect(() => {
    if (!isGeneric) return;

    const calculateOverflow = () => {
      const nextMap = {};

      visibleItems.forEach((_, rowIdx) => {
        columns.forEach((col, colIdx) => {
          if (!col?.enableHorizontalScroll) return;
          const el = scrollCellRefs.current?.[rowIdx]?.[colIdx];
          if (!el) return;
          const cellKey = `${rowIdx}-${colIdx}`;
          nextMap[cellKey] = el.scrollWidth > el.clientWidth + 1;
        });
      });

      setOverflowMap(nextMap);
    };

    const frame = window.requestAnimationFrame(calculateOverflow);
    window.addEventListener('resize', calculateOverflow);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', calculateOverflow);
    };
  }, [isGeneric, visibleItems, columns, rowHeights]);

  if (isGeneric) {
    const resolveRowId = (row, idx) => {
      if (typeof getRowId === 'function') return getRowId(row, idx);
      return row?.id ?? row?.agronomist_id ?? idx;
    };

    const isDisabled = (row) => (typeof isRowDisabled === 'function' ? !!isRowDisabled(row) : false);
    const canClickRow = (row) => !isDisabled(row) && typeof onRowClick === 'function';

    return (
      <S.ContainerTable hasGap={false}>
        <S.ContainerTableColumn>
          {columns.map((col, colIdx) => (
            <S.ContainerTableColumnV2
              key={col?.key ?? col?.label ?? colIdx}
              width={col?.width}
              minWidth={col?.minWidth}
              maxWidth={col?.maxWidth}
            >
              <S.ContainerColumnInfoTopBlack>
                <S.ContainerInfoTopLabel
                  minWidth={col?.minWidth}
                  width={col?.width ?? col?.minWidth}
                  maxWidth={col?.maxWidth}
                >
                  <TaglineP color={theme.colors.white.Branco} fontWeight={600}>
                    {col?.label ?? '-'}
                  </TaglineP>
                </S.ContainerInfoTopLabel>
              </S.ContainerColumnInfoTopBlack>

              {visibleItems.map((row, rowIdx) => {
                const id = resolveRowId(row, rowIdx);
                const cursor = canClickRow(row);
                const style = typeof getRowStyle === 'function' ? getRowStyle(row, rowIdx) : undefined;
                const enableHorizontalScroll = !!col?.enableHorizontalScroll;
                const cellKey = `${rowIdx}-${colIdx}`;
                const hasOverflow = !!overflowMap[cellKey];
                const canHorizontalScroll = enableHorizontalScroll && hasOverflow;
                const cellMinWidth = enableHorizontalScroll ? '100%' : (col?.minWidth ?? col?.width);
                const cellMaxWidthContent = col?.maxWidthContent;
                const cellWidth = col?.width;
                const cellMaxWidth = col?.maxWidth ?? cellMaxWidthContent;

                return (
                  <S.ContainerColumnCellContent
                    key={`${id}-${col?.key ?? colIdx}`}
                    firstChild={rowIdx === 0}
                    lastChild={rowIdx === visibleItems.length - 1}
                    cursor={cursor}
                    onClick={() => (cursor ? onRowClick(row) : null)}
                    maxWidthContent={col?.maxWidthContent}
                    height={rowHeights[rowIdx]}
                    style={style}
                    ref={(el) => setCellRef(rowIdx, colIdx, el)}
                  >
                    <S.TableRowCell
                      width={cellWidth}
                      minWidth={cellMinWidth}
                      maxWidth={cellMaxWidth}
                      JustifyContent={col?.justifyContent}
                      enableHorizontalScroll={canHorizontalScroll}
                      showScrollHint={hasOverflow}
                      ref={(el) => setScrollCellRef(rowIdx, colIdx, el)}
                    >
                      {typeof col?.render === 'function' ? col.render(row) : null}
                    </S.TableRowCell>
                  </S.ContainerColumnCellContent>
                );
              })}
            </S.ContainerTableColumnV2>
          ))}
        </S.ContainerTableColumn>
      </S.ContainerTable>
    );
  }

  return (
    <S.ContainerTable hasGap={false}>
      <S.ContainerTableColumn>
        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="260px" width="260px">
              <TaglineP color={theme.colors.white.Branco} fontWeight={600}>
                Nome
              </TaglineP>
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const canNavigate = isActive;
            const nome = agronomo?.agronomist_nome ?? agronomo?.nome ?? '-';

            return (
              <S.ContainerColumnCellContent
                key={`nome-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = `/agronomos/${id}`) : null)}
                height={rowHeights[idx]}
                ref={(el) => setCellRef(idx, 0, el)}
              >
                <S.TableRowCell minWidth="260px">
                  <BodyM
                    color={isActive ? theme.colors.gray.Cinza_2 : theme.colors.disabled.Desabilitado_Medio}
                    fontWeight={800}
                  >
                    {nome}
                  </BodyM>
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>

        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="170px" width="170px">
              <TaglineP color={theme.colors.white.Branco} fontWeight={600}>
                Documento
              </TaglineP>
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const canNavigate = isActive;
            const displayCreas = getDisplayCreas(agronomo);

            return (
              <S.ContainerColumnCellContent
                key={`doc-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = `/agronomos/${id}`) : null)}
                height={rowHeights[idx]}
                ref={(el) => setCellRef(idx, 1, el)}
              >
                <S.TableRowCell minWidth="170px">
                  {displayCreas.length > 0 ? (
                    <div style={cellLabelStyle}>
                      {displayCreas.map((crea, creaIndex) => {
                        const key = `${id}-doc-${crea?.id ?? creaIndex}`;
                        const isCreaActive = crea?.status === 1;
                        return (
                          <TaglineP
                            key={key}
                            color={
                              isActive
                                ? (isCreaActive ? theme.colors.gray.Cinza_2 : theme.colors.gray.Cinza_7)
                                : theme.colors.disabled.Desabilitado_Medio
                            }
                            fontWeight={600}
                            style={{ ...textInCellLabelStyle, overflowWrap: 'anywhere' }}
                          >
                            {formatCreaNumero(crea, agronomo)}
                          </TaglineP>
                        );
                      })}
                    </div>
                  ) : (
                    <TaglineP
                      color={isActive ? theme.colors.gray.Cinza_2 : theme.colors.disabled.Desabilitado_Medio}
                      fontWeight={600}
                    >
                      -
                    </TaglineP>
                  )}
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>

        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="170px" width="170px">
              <TaglineP color={theme.colors.white.Branco} fontWeight={600}>
                Tipo
              </TaglineP>
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const canNavigate = isActive;
            const displayCreas = getDisplayCreas(agronomo);

            return (
              <S.ContainerColumnCellContent
                key={`tipo-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = `/agronomos/${id}`) : null)}
                height={rowHeights[idx]}
                ref={(el) => setCellRef(idx, 2, el)}
              >
                <S.TableRowCell minWidth="170px">
                  {displayCreas.length > 0 ? (
                    <div style={cellLabelStyle}>
                      {displayCreas.map((crea, creaIndex) => {
                        const key = `${id}-tipo-${crea?.id ?? creaIndex}`;
                        const isCreaActive = crea?.status === 1;
                        return (
                          <TaglineP
                            key={key}
                            color={
                              isActive
                                ? (isCreaActive ? theme.colors.gray.Cinza_2 : theme.colors.gray.Cinza_7)
                                : theme.colors.disabled.Desabilitado_Medio
                            }
                            fontWeight={600}
                            style={{ ...textInCellLabelStyle, overflowWrap: 'anywhere' }}
                          >
                            {formatTipoRegistro(crea)}
                          </TaglineP>
                        );
                      })}
                    </div>
                  ) : (
                    <TaglineP
                      color={isActive ? theme.colors.gray.Cinza_2 : theme.colors.disabled.Desabilitado_Medio}
                      fontWeight={600}
                    >
                      -
                    </TaglineP>
                  )}
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>

        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="110px" width="110px">
              <TaglineP color={theme.colors.white.Branco} fontWeight={600}>
                UF/Registro
              </TaglineP>
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const canNavigate = isActive;
            const displayCreas = getDisplayCreas(agronomo);

            return (
              <S.ContainerColumnCellContent
                key={`uf-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = `/agronomos/${id}`) : null)}
                height={rowHeights[idx]}
                ref={(el) => setCellRef(idx, 3, el)}
              >
                <S.TableRowCell minWidth="110px">
                  {displayCreas.length > 0 ? (
                    <div style={cellLabelStyle}>
                      {displayCreas.map((crea, creaIndex) => {
                        const key = `${id}-uf-${crea?.id ?? creaIndex}`;
                        const isCreaActive = crea?.status === 1;
                        return (
                          <TaglineP
                            key={key}
                            color={
                              isActive
                                ? (isCreaActive ? theme.colors.gray.Cinza_2 : theme.colors.gray.Cinza_7)
                                : theme.colors.disabled.Desabilitado_Medio
                            }
                            fontWeight={600}
                            style={{ ...textInCellLabelStyle, overflowWrap: 'anywhere' }}
                          >
                            {formatRegistro(crea, agronomo)}
                          </TaglineP>
                        );
                      })}
                    </div>
                  ) : (
                    <TaglineP
                      color={isActive ? theme.colors.gray.Cinza_2 : theme.colors.disabled.Desabilitado_Medio}
                      fontWeight={600}
                    >
                      -
                    </TaglineP>
                  )}
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>

        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="170px" width="170px">
              <TaglineP color={theme.colors.white.Branco} fontWeight={600}>
                Status
              </TaglineP>
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const canNavigate = isActive;
            const displayCreas = getDisplayCreas(agronomo);

            return (
              <S.ContainerColumnCellContent
                key={`status-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = `/agronomos/${id}`) : null)}
                height={rowHeights[idx]}
                ref={(el) => setCellRef(idx, 4, el)}
              >
                <S.TableRowCell minWidth="170px">
                  {isActive && displayCreas.length > 0 ? (
                    <div style={cellLabelStyle}>
                      {displayCreas.map((crea, creaIndex) => {
                        const key = `${id}-status-${crea?.id ?? creaIndex}`;
                        const isCreaActive = crea?.status === 1;
                        return (
                          <TextOrStatus
                            key={key}
                            maxWidth="auto"
                            type="status"
                            status={isCreaActive ? 2 : 3}
                            textSuccess="Ativo"
                            textError="Inativo"
                            textDefault="Ativo"
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <TextOrStatus
                      maxWidth="auto"
                      type="status"
                      status={isActive ? 2 : 3}
                      textSuccess="Ativo"
                      textError="Inativo"
                      textDefault="Ativo"
                    />
                  )}
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>

        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="170px" width="170px">
              <TaglineP color={theme.colors.white.Branco} fontWeight={600}>
                Última atualização
              </TaglineP>
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const canNavigate = isActive;
            const ultimaAtualizacao = agronomo?.modified ?? agronomo?.updated_at ?? agronomo?.updatedAt ?? null;

            return (
              <S.ContainerColumnCellContent
                key={`updated-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = `/agronomos/${id}`) : null)}
                height={rowHeights[idx]}
                ref={(el) => setCellRef(idx, 5, el)}
              >
                <S.TableRowCell minWidth="170px">
                  <TaglineP
                    color={isActive ? theme.colors.gray.Cinza_2 : theme.colors.disabled.Desabilitado_Medio}
                    fontWeight={600}
                  >
                    {ultimaAtualizacao ? `Atualizado ${formatarData(ultimaAtualizacao)}` : '-'}
                  </TaglineP>
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>
      </S.ContainerTableColumn>
    </S.ContainerTable>
  );
};

export default TableWithLabelBlackV2;
